'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** cn_vertex : a vertex to draw walls
//***********************************************************************************
//***********************************************************************************

import { cn_element } from './cn_element';
import { CN_WALL_COSINE_SHAPE_THRESHOLD } from './cn_model_constants';
import { cn_angle_sensitive_area, cn_camera } from '../svg/cn_camera';
import {
    cn_clone,
    cn_mul,
    cn_polar,
    cn_sub,
    cn_dist,
    cn_cart,
    cn_normalize,
    DATATION,
    cn_dot,
    cn_normal,
    cn_add,
    cn_compute_intersection_position,
    cn_box,
    cn_intersect_line,
    cn_middle
} from '../utils/cn_utilities';
import { logger } from '../utils/cn_logger';
import { cn_transaction_manager } from '../utils/cn_transaction_manager';

export class cn_sector {
    constructor(w) {
        this.wall = w;
        this.angle = 0;
    }
}

function sector_by_angle(s0, s1) {
    if (s0.angle < s1.angle) return -1;
    return 1;
}

export class cn_vertex extends cn_element {
    constructor(p, is_3d = false, parent) {
        super(parent);
        this.class_name = 'cn_vertex';
        this.position = [p[0], p[1]];
        if (is_3d) {
            if (p.length >= 3) this.position.push(p[2]);
            else this.position.push(0);
        }
        this.walls = [];
        this.angles = [];
        this.draw_priority = 10;

        this.support_wall = null;
        this.support_wall_side = 0;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        return [...this.position];
    }

    static unserialize(json, scene) {
        if (typeof (json) != 'object') return false;
        var vertex = new cn_vertex(json, json.length === 3, scene);
        scene.vertices.push(vertex);
        vertex.parent = scene;
        return vertex;
    }

    //***********************************************************************************
    //**** Topological operations
    //***********************************************************************************
    /**
     * connects to given wall
     * @param {import('./cn_wall').cn_wall} wall
     * @param {cn_transaction_manager} transaction_manager
     */
    set_support_wall(wall, side, transaction_manager) {
        if (this.support_wall != wall || this.support_wall_side != side) {
            if (this.support_wall && this.support_wall != wall)
                this.support_wall.disconnect_vertex(this, transaction_manager);
            if (transaction_manager) transaction_manager.push_item_set(this, ["support_wall", "support_wall_side"]);
            this.support_wall = wall;
            this.support_wall_side = side;
        }
        if (this.support_wall)
            this.support_wall.connect_vertex(this, transaction_manager);
    }

    /**
     * Computes the cutting plane for walls
     * @returns {{position: number[], normal: number[]}}
     */
    compute_cutting_plane() {
        if (this.walls.length == 2) {
            const d0 = this.walls[0].direction_from(this);
            const d1 = this.walls[1].direction_to(this);
            const pscal = cn_dot(d0, d1);
            if (pscal < - CN_WALL_COSINE_SHAPE_THRESHOLD) return null;
            const vi0 = this.walls[0].vertex_index(this);
            const p00 = this.walls[0].shape[(vi0) ? 2 : 0];
            const p01 = this.walls[0].shape[(vi0) ? 3 : 1];
            const vi1 = this.walls[1].vertex_index(this);
            const p10 = this.walls[1].shape[(vi1) ? 3 : 1];
            const p11 = this.walls[1].shape[(vi1) ? 2 : 0];
            let p0 = [0, 0];
            let p1 = [0, 0];
            if (pscal > CN_WALL_COSINE_SHAPE_THRESHOLD) {
                p0 = cn_middle(p00, p10);
                p1 = cn_middle(p01, p11);
            }
            else {
                p0 = cn_intersect_line(p00, d0, p10, d1);
                p1 = cn_intersect_line(p01, d0, p11, d1);
                if (!p0 || !p1) return null;
            }
            const dir = cn_sub(p1, p0);
            if (cn_normalize(dir) < 0.001) return null;
            return { position: p0, normal: cn_normal(dir) };
        }
        else if (this.support_wall) {
            const wall_side = this.support_wall.get_side(this.support_wall_side);
            return { position: wall_side[0], normal: cn_normal(wall_side[1]) };
        }
        return null;
    }

    /**
     * Returns the part segment of the tip of the wall that is supported by the support wall
     * @returns {number[][]}
     */
    get_support_segment() {
        if (!this.support_wall) return null;
        if (this.walls.length != 1) return null;
        const wall = this.walls[0];
        const vi = wall.vertex_index(this);
        const segment = [wall.shape[(vi == 0) ? 0 : 2], wall.shape[(vi == 0) ? 1 : 3]];
        const shape_segment = this.support_wall.get_side_segment(this.support_wall_side);
        const dir = cn_sub(shape_segment[1], shape_segment[0]);
        const l = cn_normalize(dir);
        const x0 = cn_dot(dir, cn_sub(segment[0], shape_segment[0]));
        if (x0 < 0) segment[0] = shape_segment[0];
        if (x0 > l) return null;
        const x1 = cn_dot(dir, cn_sub(segment[1], shape_segment[0]));
        if (x1 > l) segment[1] = shape_segment[1];
        if (x1 < 0) return null;
        return segment;
    }

    /**
     * Returns true if the vertex can be supported by a wall
     * @returns {boolean}
     */
    is_free() {
        return this.walls.length < 2 && !this.support_wall;
    }

    /**
     * Tries to fix a given support wall fort eh single vertex
     * @param {import('./cn_wall').cn_wall} support_wall
     * @param {cn_transaction_manager} transaction_manager
     * @returns {boolean}
     */
    check_support_wall(support_wall, transaction_manager) {
        if (this.walls.length != 1) return false;

        const wall = this.walls[0];
        const v1 = wall.other_vertex(this);
        const impact = support_wall.raytrace_support_shape(v1.position, wall.direction_to(this));
        //*** Maybe the wall can still be OK? */
        if (impact && impact.distance > 0.01) {
            this.set_support_wall(support_wall, impact.side, transaction_manager);
            wall.adjust_shape(wall.vertex_index(this));
            if (transaction_manager) transaction_manager.push_item_set(this, "position");
            this.position = impact.point;
            return true;
        }
        //*** Maybe not */
        this.set_support_wall(null, 0, transaction_manager);
        return false;
    }

    dummy_clone() {
        let vertex = new cn_vertex(this.position, false, null);
        vertex.walls = this.walls.concat([]);
        vertex.support_wall = this.support_wall;
        vertex.support_wall_side = this.support_wall_side;
        return vertex;
    }
    //***********************************************************************************
    //**** Draw the vertex in svg
    //***********************************************************************************
    draw(camera, add_classes) {
        var html = '';
        var p = camera.world_to_screen(this.position);
        var draw_class = 'vertex';
        if (this.walls.some(w => w.locked)) draw_class += ' locked';
        var radius = 3;
        var is_selected = false;
        var is_mouseover = false;
        if (add_classes && add_classes.length) {
            add_classes.forEach(ac => {
                if (ac == 'selected')
                    is_selected = true;
                else if (ac == 'mouseover')
                    is_mouseover = true;
                else
                    draw_class += ' ' + ac;
            });
        }

        //** draw vertex */
        var dc = draw_class;
        var radius = 3;
        if (is_selected) {
            dc += ' selected';
            radius = 5;
        }
        if (is_mouseover) {
            dc += ' mouseover';
            radius = 5;
        }
        html += '<circle class=\'' + dc + '\' cx=\'' + p[0] + '\' cy=\'' + p[1] + '\' r=\'' + radius + '\' />';

        return html;
    }

    //******************************************************
    /**
     * Draws angles of the vertex
     * @param {cn_camera} camera
     * @param {cn_angle_sensitive_area[]} sensitive_areas
     * @returns {string}
     */
    draw_angles(camera, sensitive_areas = null) {

        if (this.walls.length < 2 && !this.support_wall) return "";

        var sectors = [];

        //*** compute sector angles
        for (var i in this.walls) {
            var w = this.walls[i];
            var sector = new cn_sector(w);
            sector.angle = cn_polar(w.direction_from(this))[1];
            sectors.push(sector);
        }

        if (this.support_wall) {
            var sector = new cn_sector(this.support_wall);
            sector.angle = cn_polar(this.support_wall.bounds.direction)[1];
            sectors.push(sector);
            sector = new cn_sector(this.support_wall);
            sector.angle = cn_polar(this.support_wall.bounds.direction)[1] + Math.PI;
            sectors.push(sector);
        }

        //*** sort sectors by angle
        sectors.sort(sector_by_angle);
        const l = sectors.length;

        var html = '';
        let nbsa = 0;
        let this_vertex = this;
        function apply_sa(wall) {
            if (!wall.vertices.includes(this_vertex)) return null;
            if (wall.locked) return null;
            let sa = null;
            if (nbsa >= sensitive_areas.length)
                sensitive_areas.push(new cn_angle_sensitive_area());
            sa = sensitive_areas[nbsa];
            nbsa++;
            sa["wall"] = wall;
            sa["vertex"] = this_vertex;
            return sa;
        }

        for (var ni = 0; ni < l; ni++) {
            var a0 = sectors[ni].angle;
            var a1 = sectors[(ni + 1) % l].angle;
            let sa0 = null;
            let sa1 = null;
            if (sensitive_areas !== null) {
                sa0 = apply_sa(sectors[ni].wall);
                sa1 = apply_sa(sectors[(ni + 1) % l].wall);
            }
            html += camera.draw_svg_angle(this.position, cn_cart([1, a0]), cn_cart([1, a1]), sa0, sa1);
        }
        if (sensitive_areas && sensitive_areas.length > nbsa)
            sensitive_areas.splice(nbsa, sensitive_areas.length - nbsa);
        return html;
    }

    //******************************************************
    //*** update sectors
    //******************************************************
    update() {
        var sectors = [];

        //*** compute sector angles
        for (var i in this.walls) {
            var w = this.walls[i];
            var sector = new cn_sector(w);
            //*** for direction, we use actual vertex positions, for stability questions */
            var direction = cn_sub(w.vertices[0].position, w.vertices[1].position);
            if (w.vertices[0] != this) direction = cn_mul(direction, -1);
            var pol = cn_polar(direction);
            sector.angle = pol[1];
            sectors.push(sector);
        }

        //*** sort sectors by angle
        sectors.sort(sector_by_angle);

        this.walls = [];
        this.angles = [];
        for (var i in sectors) {
            this.walls.push(sectors[i].wall);
            this.angles.push(sectors[i].angle);
        }

        this.removable = (this.walls.length == 2);
    }

    //******************************************************
    //*** get next wall
    //******************************************************
    next_wall(wall, avoid_balcony = false) {
        if (this.walls.length == 1) return wall;
        var index = this.walls.indexOf(wall);
        if (index < 0) return null;
        for (var niter = 0; niter < this.walls.length - 1; niter++) {
            index++;
            if (index >= this.walls.length) index = 0;
            if (avoid_balcony && wall.balcony != this.walls[index].balcony) continue;
            return this.walls[index];
        }
        return wall;
    }

    previous_wall(wall, avoid_balcony = false) {
        if (this.walls.length == 1) return wall;
        var index = this.walls.indexOf(wall);
        if (index < 0) return null;
        for (var niter = 0; niter < this.walls.length - 1; niter++) {
            index--;
            if (index < 0) index = this.walls.length - 1;
            if (avoid_balcony && wall.balcony != this.walls[index].balcony) continue;
            return this.walls[index];
        }
        return wall;
    }

    //******************************************************
    //*** get wall to
    //******************************************************
    wall_to(vertex) {
        for (var i in this.walls) {
            if (this.walls[i].vertices[0] == this && this.walls[i].vertices[1] == vertex)
                return this.walls[i];
            if (this.walls[i].vertices[1] == this && this.walls[i].vertices[0] == vertex)
                return this.walls[i];
        }
        return null;
    }

    //******************************************************
    //*** Compute angle from wall to next
    //******************************************************
    angle_between(wall0, wall1) {
        var i0 = this.walls.indexOf(wall0);
        if (i0 < 0) return 0;
        var i1 = this.walls.indexOf(wall1);
        if (i1 < 0) return 0;
        var angle = this.angles[i1] - this.angles[i0];
        while (angle < 0) angle += 2 * Math.PI;
        return angle;
    }

    /**
     * Returns true if vertex is close to input point.
     * @param {number[]} point
     * @param {number} tolerance
     * @returns {boolean}
     */
    contains(point, tolerance) {
        if (cn_dist(this.position, point) <= tolerance)
            return true;
        return false;
    }

    //******************************************************
    /**
     * Returns an object containing basic vertex element on walls
     * @returns {object}
     */
    display_walls() {
        var obj = {};
        obj.position = cn_clone(this.position);
        obj.position.push(this.ID);
        for (var i in this.walls) {
            var w = this.walls[i];
            var vtx0 = cn_clone(w.vertices[0].position);
            vtx0.push(w.vertices[0].ID);
            var vtx1 = cn_clone(w.vertices[1].position);
            vtx1.push(w.vertices[1].ID);
            obj['wall_' + i] = [vtx0, vtx1, w.ID];
        }
        return obj;
    }

    /**
     * Performs a vertex operation
     * @param {function} operation
     */
    vertex_operation(operation) {
        operation(this.position);
    }

    /**
     * Returns bounding box
     * @returns {cn_box}
     */
    get_bounding_box() {
        const box = new cn_box();
        box.enlarge_point(this.position);
        return box;
    }

    /**
     * Returns true if at least one wall is locked
     * @returns {boolean}
     */
    is_locked() {
        return this.walls.some(w => w.locked);
    }

    /**
     * returns true if wall geometry hasn't changed since date
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date_geometry(date) {
        return !this.walls.some(w => !w.up_to_date_vertex_geometry(date));
    }
}

